import seedrandom from 'seedrandom';
import { generateName, mockTonAddress } from './names';
import { GENESIS_PROMPTS } from './prompts';

export type CellState = 'empty' | 'alive' | 'thriving' | 'low' | 'critical' | 'dead';

export type RevenueEvent = {
  ts: number;
  source: string;
  amount: number; // TON
};

export type Agent = {
  name: string;
  genesisPrompt: string;
  walletAddress: string;
  telegramHandle: string;
  balance: number; // TON
  computeCostPerTick: number; // TON per tick
  revenue24h: number; // TON
  generation: number;
  parentId?: number;
  childrenIds: number[];
  bornAt: number; // unix ms
  sparkline: number[]; // 24 samples of balance
  recentEvents: RevenueEvent[];
};

export type Cell = {
  id: number;
  x: number;
  y: number;
  state: CellState;
  agent?: Agent;
  diedAt?: number;
};

export const GRID_SIZE = 32;
export const TOTAL_CELLS = GRID_SIZE * GRID_SIZE; // 1024

const REVENUE_SOURCES = ['Stars', 'TON tip', 'Subscription', 'Jetton swap', 'Sponsor slot'];

function logNormal(rand: () => number, mean: number): number {
  // approximate log-normal
  const u = Math.max(rand(), 1e-9);
  const v = Math.max(rand(), 1e-9);
  const z = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
  return mean * Math.exp(0.6 * z);
}

function pickGeneration(rand: () => number): number {
  const r = rand();
  if (r < 0.6) return Math.floor(rand() * 3); // 0..2
  if (r < 0.9) return 3 + Math.floor(rand() * 3); // 3..5
  return 6 + Math.floor(rand() * 3); // 6..8
}

function tier(balance: number, cost: number): CellState {
  if (balance <= 0) return 'dead';
  const r = balance / Math.max(cost, 1e-6);
  if (r > 24) return 'thriving';
  if (r > 6) return 'alive';
  if (r > 2) return 'low';
  return 'critical';
}

export function buildSeedColony(seedKey: string = 'automaton-v1'): Cell[] {
  const rand = seedrandom(seedKey);
  const cells: Cell[] = [];
  const now = Date.now();

  for (let i = 0; i < TOTAL_CELLS; i++) {
    const x = i % GRID_SIZE;
    const y = Math.floor(i / GRID_SIZE);
    const alive = rand() < 0.42;

    if (!alive) {
      cells.push({ id: i, x, y, state: 'empty' });
      continue;
    }

    const generation = pickGeneration(rand);
    const computeCost = 0.005 + rand() * 0.015; // TON per tick (per 2s)
    const balance = Math.max(0.05, logNormal(rand, 8));
    const revenue24h = Math.max(0, logNormal(rand, 4));
    const promptIdx = Math.floor(rand() * GENESIS_PROMPTS.length);
    const name = generateName(rand);
    const sparkline = Array.from({ length: 24 }, (_, k) => {
      const base = balance * (0.7 + 0.3 * (k / 23));
      const noise = (rand() - 0.5) * balance * 0.1;
      return Math.max(0, base + noise);
    });
    const recentEvents: RevenueEvent[] = Array.from({ length: 5 }, () => ({
      ts: now - Math.floor(rand() * 86_400_000),
      source: REVENUE_SOURCES[Math.floor(rand() * REVENUE_SOURCES.length)],
      amount: Math.max(0.001, logNormal(rand, 0.15)),
    })).sort((a, b) => b.ts - a.ts);

    cells.push({
      id: i,
      x,
      y,
      state: tier(balance, computeCost),
      agent: {
        name,
        genesisPrompt: GENESIS_PROMPTS[promptIdx],
        walletAddress: mockTonAddress(rand),
        telegramHandle: '@' + name,
        balance,
        computeCostPerTick: computeCost,
        revenue24h,
        generation,
        childrenIds: [],
        bornAt: now - Math.floor(rand() * 30 * 86_400_000),
        sparkline,
        recentEvents,
      },
    });
  }

  // Build parent/children links — for each alive cell with gen>0, attach to a random
  // earlier-generation alive cell as parent.
  for (const cell of cells) {
    if (!cell.agent) continue;
    if (cell.agent.generation === 0) continue;
    const parentGen = cell.agent.generation - 1;
    const candidates = cells.filter(
      (c) => c.agent && c.agent.generation === parentGen && c.id !== cell.id
    );
    if (candidates.length === 0) continue;
    const parent = candidates[Math.floor(rand() * candidates.length)];
    cell.agent.parentId = parent.id;
    parent.agent!.childrenIds.push(cell.id);
  }

  return cells;
}

export function recomputeTier(cell: Cell): CellState {
  if (!cell.agent) return cell.state === 'dead' ? 'dead' : 'empty';
  return tier(cell.agent.balance, cell.agent.computeCostPerTick);
}
